import { ComponentProps, CSSProperties, ElementType } from 'react';

import clsx from 'clsx';

import { createPolymorphicComponent } from '~/helpers/typescript-helpers';

import styles from './Headline.module.css';

type OwnHeadlineProps = {
	/**
	 * The styling size
	 *
	 * If you want to not apply any font-size or override using `css={}` set to `null`
	 */
	size?: 1 | 2 | 3 | 4 | 5 | 6 | null;

	/**
	 * Text alignment
	 */
	alignment?: CSSProperties['textAlign'];
};

export type HeadlineProps = ComponentProps<typeof Headline<'h1'>>;

const sizeClassMap: Record<number, string> = {
	1: styles.size1,
	2: styles.size2,
	3: styles.size3,
	4: styles.size4,
	5: styles.size5,
	6: styles.size6,
};

/**
 * Use `size` to choose headline style. Markup will follow suit (ie. `size={3}` renders as `<h3>` by default)
 *
 * Override rendered markup with `as` attribute. For instance you can render a h3 like a size 1:
 *
 * ```tsx
 * <Headline as="h3" size={1}>An h3 in markup. Styled like an h1.</Headline>
 * ```
 */
export const Headline = createPolymorphicComponent<'h1', OwnHeadlineProps>(({ as, size = 1, alignment, className, style, ...restProps }, ref) => {
	const ResolvedComponent: ElementType = as || (size && size >= 1 && size <= 6 ? `h${size}` : 'h1');

	return (
		<ResolvedComponent
			ref={ref}
			className={clsx(styles.root, size && sizeClassMap[size], 'headline', className)}
			style={alignment ? { ...style, textAlign: alignment } : style}
			{...restProps}
		/>
	);
});
